import React, {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faHeart, faPlay} from '@fortawesome/free-solid-svg-icons';

const SpotifySongs = ({ artistName, artistEngName, songCatName, artistMode, artistModeSongs, defaultPlatform, selectedLan, onIdsCollected }) => {
  const [songs, setSongs] = useState([]);
  const [songsList, setSongsList] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    songs.forEach(song => {
      if (onIdsCollected) onIdsCollected(String(song.trackId), song);
    });
    // onIdsCollected intentionally omitted — it's a stable useCallback wrapper;
    // reporting should only fire when songs change, not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songs]);

  useEffect(() => {
    const fetchSongs = async () => {
      const country = selectedLan?.market || 'US';
      const searchTerm = encodeURIComponent(artistName);
      const limit = 50;

      const isLocal = window.location.hostname === 'localhost';
      const itunesUrl = isLocal
        ? `https://itunes.apple.com/search?term=${searchTerm}&media=music&entity=song&limit=${limit}&country=${country}&attribute=artistTerm`
        : `/.netlify/functions/itunes?term=${searchTerm}&country=${country}`;

      const maxRetries = 3;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const res = await fetch(itunesUrl);
          if (!res.ok) {
            const err = new Error(`HTTP ${res.status}`);
            err.status = res.status;
            throw err;
          }
          const data = await res.json();
          const normalize = (s) => s?.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '') || '';
          const searchNorm = normalize(artistName);
          const hasCJK = /[぀-鿿가-힯]/u.test(artistName);

          let tracks = data.results.filter(t => {
            const aNorm = normalize(t.artistName);
            return aNorm.includes(searchNorm) || searchNorm.includes(aNorm);
          });

          // Fallback 1: CJK character overlap (handles most trad/simplified variants e.g. 杨千嬅↔楊千嬅)
          if (tracks.length === 0 && hasCJK && searchNorm.length > 0) {
            const searchChars = new Set([...searchNorm]);
            tracks = data.results.filter(t =>
              [...normalize(t.artistName)].some(c => searchChars.has(c))
            );
          }

          // Fallback 2: trust iTunes entirely for CJK — covers cases where ALL characters
          // differ between simplified/traditional (e.g. 萧亚轩↔蕭亞軒).
          // Safe because we already searched with attribute=artistTerm.
          if (tracks.length === 0 && hasCJK) {
            tracks = data.results;
          }

          if (tracks.length === 0) {
            setError(`No songs found for ${artistName}`);
            return;
          }
          let listNumber = 1;
          if (artistMode) listNumber = artistModeSongs;
          const randomTracks = getRandomSongs(tracks, listNumber);
          setSongs(randomTracks);
          setSongsList(randomTracks);
          return;
        } catch (err) {
          const isRetryable = err?.status === 429 || err?.status === 503;
          if (isRetryable && attempt < maxRetries) {
            const delay = Math.pow(2, attempt + 1) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
          } else {
            console.error('iTunes fetch error:', err);
            setError(`Error: ${err?.message || 'failed to load songs'}`);
            return;
          }
        }
      }
    };

    const getRandomSongs = (trackList, num) => {
      const list = [...trackList];
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
      let randomNumber = Math.floor(Math.random() * list.length);
      if (randomNumber > list.length - num) randomNumber = randomNumber - num;
      if (randomNumber < 0) randomNumber = 0;
      return list.slice(randomNumber, randomNumber + num);
    };

    fetchSongs();
  }, [artistName, artistEngName, songCatName, artistMode, artistModeSongs, defaultPlatform, selectedLan]);

  return (
    <div>
      {error && <div>{error}</div>}
      {!error && songsList.length > 0 ? (
        songsList
          .filter((song) => song.id !== 'error')
          .map((song) => {
            const playUrl = defaultPlatform === 'iTunes'
              ? song.trackViewUrl
              : `https://www.youtube.com/results?search_query=${encodeURIComponent(artistName + ' ' + song.trackName)}`;
            const albumUrl = defaultPlatform === 'iTunes'
              ? song.collectionViewUrl
              : `https://www.youtube.com/results?search_query=${encodeURIComponent(artistName + ' ' + song.collectionName)}`;
            const artworkUrl = song.artworkUrl100?.replace('100x100', '600x600');
            const releaseYear = song.releaseDate ? song.releaseDate.substring(0, 4) : '';
            const trackId = String(song.trackId);

            return (
              <div key={trackId}
                   className={`flex justify-between gap-x-6 py-5 ${artistMode && 'border-b border-gray-500'}`}>
                <div className="flex min-w-0 gap-x-4 text-xs">
                  {artworkUrl && (
                    <div className="min-w-0 flex-none">
                      <a href={playUrl} target='_blank' rel="noreferrer"
                         className="font-medium text-white hover:text-gray-400"
                         artist={artistName} song={song.trackName} spotify={trackId}>
                        <img className="Album_Icon" src={artworkUrl}
                             alt={`${song.trackName} album cover`} loading="lazy"/>
                      </a>
                    </div>
                  )}
                  <div className="min-w-0 flex-auto">
                    <p className="text-base xs:text-sm font-semibold leading-6 text-white">
                      <a href={playUrl} target='_blank' rel="noreferrer"
                         className="font-medium text-white hyphens-auto hover:text-gray-400"
                         artist={artistName} song={song.trackName} spotify={trackId}>
                        {song.trackName}
                      </a>
                    </p>
                    <p className="mt-1 text-sm font-semibold leading-5 text-white">
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a href="#" rel="noreferrer"
                         className="font-medium text-white hyphens-auto hover:text-gray-400"
                         artist={artistName} artistengname={song.artistName} song={song.trackName} spotify={trackId}>
                        {artistName} {artistName === artistEngName ? '' : `(${song.artistName})`}
                      </a>
                    </p>
                    <p className="text-xs font-semibold leading-5 text-gray-400">
                      <a href={albumUrl} target='_blank' rel="noreferrer"
                         className="font-medium hyphens-auto hover:text-gray-200"
                         artist={artistName} song={song.trackName} spotify={trackId}>
                        {song.collectionName}
                      </a> ({releaseYear})
                    </p>
                  </div>
                </div>
                <div className="shrink-0 flex-col items-end grid grid-cols-2 gap-2 content-start">
                  <div className="text-center">
                    <p className="text-base leading-6 text-white">
                      <FontAwesomeIcon icon={faHeart} className="cursor-pointer hover:text-red-600"
                                       clicked='false' artist={artistName} song={song.trackName}
                                       spotify={trackId} size='lg'/>
                    </p>
                    <p className="text-base leading-6 text-white mt-2">
                      <a href={playUrl} target='_blank' rel="noreferrer"
                         className="font-medium text-white hover:text-gray-400"
                         artist={artistName} song={song.trackName} spotify={trackId}>
                        <FontAwesomeIcon icon={faPlay} size='lg'/>
                      </a>
                    </p>
                  </div>
                  <div>
                    <p className="text-base leading-6 text-white">
                      <span id={trackId}>0</span>
                    </p>
                    <p className="text-base leading-6 text-white mt-2">
                      <span id={`c_${trackId}`}>0</span>
                    </p>
                  </div>
                </div>
              </div>
            );
          })
      ) : (
        !error && <p>Loading {artistName}...</p>
      )}
    </div>
  );
};

export default SpotifySongs;
